import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DirectBookingPage } from '../modules/erp';
import { DirectBookingService, DEMO_PRESETS } from '../modules/erp/components/DirectBookingPage';
import { Appointment, StorefrontProfile, StaffMember, NicheKey } from '../modules/erp/types';
import { supabase } from '../lib/supabase';
import { mapSupabaseStaffMember } from '../hooks/sync/syncMappers';
import { getLocalDateString } from '../modules/erp/utils/calendarUtils';
import { Calendar, Store, ArrowLeft, Loader2, MessageCircle } from 'lucide-react';
import { extractDominantColor } from '../utils/colorExtractor';

export default function PublicDirectBookingPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<StorefrontProfile | null>(null);
  const [services, setServices] = useState<DirectBookingService[]>([]);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isDemoPreset, setIsDemoPreset] = useState(false);
  const [demoNicheKey, setDemoNicheKey] = useState<NicheKey>('estetica');
  const [notFound, setNotFound] = useState(false);

  const loadStorefrontData = useCallback(async () => {
    setLoading(true);
    setNotFound(false);

    const targetSlug = slug ? slug.toLowerCase().trim() : 'demo';

    // 1. Cenários de Demonstração / Testes Rápidos
    if (targetSlug === 'demo' || targetSlug === 'studio-bella' || targetSlug === 'salao') {
      setIsDemoPreset(true);
      setDemoNicheKey('estetica');
      setProfile(DEMO_PRESETS.estetica.profile);
      setServices(DEMO_PRESETS.estetica.services);
      setStaffList(DEMO_PRESETS.estetica.staff);
      setLoading(false);
      return;
    }

    if (targetSlug === 'fabio-eletricista' || targetSlug === 'eletricista') {
      setIsDemoPreset(true);
      setDemoNicheKey('eletricista');
      setProfile(DEMO_PRESETS.eletricista.profile);
      setServices(DEMO_PRESETS.eletricista.services);
      setStaffList(DEMO_PRESETS.eletricista.staff);
      setLoading(false);
      return;
    }

    if (targetSlug === 'polar-climatizacao' || targetSlug === 'polar' || targetSlug === 'hvac') {
      setIsDemoPreset(true);
      setDemoNicheKey('hvac');
      setProfile(DEMO_PRESETS.hvac.profile);
      setServices(DEMO_PRESETS.hvac.services);
      setStaffList(DEMO_PRESETS.hvac.staff);
      setLoading(false);
      return;
    }

    // 2. Busca Real no Banco Supabase
    try {
      const { data: storefrontData, error: profileErr } = await supabase
        .from('storefront_profiles')
        .select('*')
        .eq('slug', targetSlug)
        .maybeSingle();

      if (profileErr || !storefrontData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Prioridade: accent_color do próprio perfil da vitrine, senão busca de branding
      let effectiveAccentColor = storefrontData.accent_color;
      let effectiveLogoUrl = storefrontData.logo_url;

      // 1. Fallback em user_business_profiles
      if (!effectiveLogoUrl) {
        try {
          const { data: bizData } = await supabase
            .from('user_business_profiles')
            .select('logo_url')
            .eq('user_id', storefrontData.user_id)
            .maybeSingle();

          if (bizData?.logo_url) {
            effectiveLogoUrl = bizData.logo_url;
          }
        } catch (bizErr) {
          console.warn('user_business_profiles fallback não disponível:', bizErr);
        }
      }

      // 2. Fallback em profiles
      if (!effectiveLogoUrl) {
        try {
          const { data: profData } = await supabase
            .from('profiles')
            .select('logo_url')
            .eq('id', storefrontData.user_id)
            .maybeSingle();

          if (profData?.logo_url) {
            effectiveLogoUrl = profData.logo_url;
          }
        } catch (profErr) {
          console.warn('profiles fallback não disponível:', profErr);
        }
      }

      // 3. Fallback de URL pública direta no bucket logos do Supabase Storage
      if (!effectiveLogoUrl && storefrontData.user_id) {
        try {
          const { data: storageData } = supabase.storage
            .from('logos')
            .getPublicUrl(`logo_${storefrontData.user_id}.jpg`);
          if (storageData?.publicUrl) {
            effectiveLogoUrl = storageData.publicUrl;
          }
        } catch (storageErr) {
          console.warn('storage logos fallback não disponível:', storageErr);
        }
      }

      // 4. Extração automática da paleta de cores dominante da logo se accent_color for padrão ou vazia
      if (effectiveLogoUrl && (!effectiveAccentColor || effectiveAccentColor === '#10b981')) {
        try {
          const palette = await extractDominantColor(effectiveLogoUrl);
          if (palette && palette.primary && palette.primary !== '#059669') {
            effectiveAccentColor = palette.primary;
          }
        } catch (colorErr) {
          console.warn('Extração de cor da logo não concluída:', colorErr);
        }
      }

      const realProfile: StorefrontProfile = {
        id: storefrontData.id,
        userId: storefrontData.user_id,
        slug: storefrontData.slug,
        displayName: storefrontData.display_name || 'Salão & Estética',
        tagline: storefrontData.tagline || 'Agendamento Online',
        bio: storefrontData.bio || undefined,
        yearsExperience: storefrontData.years_experience || undefined,
        nicheBadges: Array.isArray(storefrontData.niche_badges) ? storefrontData.niche_badges : [],
        whatsappNumber: storefrontData.whatsapp_number,
        instagramHandle: storefrontData.instagram_handle || undefined,
        facebookHandle: storefrontData.facebook_handle || undefined,
        tiktokHandle: storefrontData.tiktok_handle || undefined,
        email: storefrontData.email || undefined,
        addressDisplay: storefrontData.address_display || undefined,
        accentColor: effectiveAccentColor || '#10b981',
        logoUrl: effectiveLogoUrl || undefined,
        coverUrl: storefrontData.cover_url || undefined,
        businessHours: storefrontData.business_hours || 'Terça a Sábado: 08:00 às 19:00',
        isPublished: Boolean(storefrontData.is_published),
        viewCount: Number(storefrontData.view_count || 0),
        createdAt: storefrontData.created_at,
        updatedAt: storefrontData.updated_at
      };

      // Limita a busca para os próximos 60 dias (evita carregar anos de histórico no banco)
      const maxBookingDate = new Date();
      maxBookingDate.setDate(maxBookingDate.getDate() + 60);
      const maxFutureDateString = maxBookingDate.toISOString().slice(0, 10);

      // Busca serviços, equipe e agendamentos ocupados em paralelo com limits defensivos e colunas estritas (Zero Vazamento LGPD)
      const [servicesRes, staffRes, apptsRes] = await Promise.all([
        supabase.from('simple_services').select('id, name, type, value, duration_minutes, description, promo, promo_config, allowed_staff_ids').eq('user_id', storefrontData.user_id).limit(100),
        supabase.from('staff_members').select('id, user_id, name, role, color, active, compensation_type, commission_percent, avatar_url, service_ids').eq('user_id', storefrontData.user_id).eq('active', true).order('created_at', { ascending: true }).limit(50),
        supabase.from('appointments').select('id, user_id, scheduled_date, scheduled_time, duration_minutes, staff_member_id, all_day, status').eq('user_id', storefrontData.user_id).gte('scheduled_date', getLocalDateString()).lte('scheduled_date', maxFutureDateString).limit(300)
      ]);

      let mappedServices: DirectBookingService[] = (servicesRes.data || []).map((s: any) => {
        const matchMin = s.description?.match(/(\d+)\s*min/);
        const duration = (typeof s.duration_minutes === 'number' && s.duration_minutes > 0)
          ? s.duration_minutes
          : (matchMin ? parseInt(matchMin[1], 10) : 60);
        return {
          id: s.id,
          name: s.name,
          category: s.type === 'hourly' ? 'Serviços por Hora' : 'Tabela de Serviços',
          price: Number(s.value || 0),
          durationMinutes: duration,
          description: s.description || undefined,
          promo: s.promo || s.promo_config || undefined,
          allowedStaffIds: Array.isArray(s.allowed_staff_ids)
            ? s.allowed_staff_ids
            : (Array.isArray(s.promo_config?.allowedStaffIds) ? s.promo_config.allowedStaffIds : undefined)
        };
      });

      let mappedStaff: StaffMember[] = (staffRes.data || []).map(mapSupabaseStaffMember);

      if (mappedStaff.length === 0) {
        mappedStaff = [
          { 
            id: 'st_solo', 
            name: realProfile.displayName || 'Profissional Responsável', 
            role: realProfile.tagline || 'Atendimento com hora marcada', 
            color: 'emerald', 
            commissionPercent: 100, 
            active: true 
          }
        ];
      }

      // Mapeamento blindado: nunca expõe dados de clientes para o navegador do visitante anônimo
      const mappedAppts: Appointment[] = (apptsRes.data || []).map((a: any) => ({
        id: a.id,
        userId: a.user_id,
        clientName: 'Horário Reservado',
        clientPhone: undefined,
        serviceName: 'Atendimento',
        durationMinutes: a.duration_minutes || 60,
        scheduledDate: a.scheduled_date,
        scheduledTime: a.scheduled_time ? a.scheduled_time.slice(0, 5) : '09:00',
        price: 0,
        status: a.status || 'confirmed',
        staffMemberId: a.staff_member_id || undefined,
        allDay: Boolean(a.all_day || (a.duration_minutes && a.duration_minutes >= 720)),
        createdAt: a.scheduled_date,
        updatedAt: a.scheduled_date
      }));

      setIsDemoPreset(false);
      setProfile(realProfile);
      setServices(mappedServices);
      setStaffList(mappedStaff);
      setAppointments(mappedAppts);
      setLoading(false);
    } catch (err) {
      console.error('Falha geral ao carregar dados da vitrine:', err);
      setNotFound(true);
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadStorefrontData();
  }, [loadStorefrontData]);

  const handleBookAppointment = async (newAppt: Appointment) => {
    // 1. Atualiza estado local imediatamente
    setAppointments(prev => [newAppt, ...prev]);

    // 2. Se for vitrine real com user_id, salva no Supabase
    if (profile?.userId && !isDemoPreset) {
      try {
        const isUUID = newAppt.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(newAppt.id);
        const finalId = isUUID ? newAppt.id : crypto.randomUUID();

        let effectiveStaffId: string | null = null;
        if (newAppt.staffMemberId) {
          if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(newAppt.staffMemberId)) {
            effectiveStaffId = newAppt.staffMemberId;
          } else {
            const matchedStaff = staffList.find(s => 
              s.id === newAppt.staffMemberId || 
              s.accessToken === newAppt.staffMemberId || 
              (s.name && newAppt.staffMemberName && s.name.toLowerCase() === newAppt.staffMemberName.toLowerCase())
            );
            if (matchedStaff?.id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(matchedStaff.id)) {
              effectiveStaffId = matchedStaff.id;
            }
          }
        }

        // Verificação anti-concorrência / conflito de última hora antes de gravar
        const { data: recentConflicts } = await supabase
          .from('appointments')
          .select('id, staff_member_id, scheduled_time, duration_minutes, all_day, status')
          .eq('user_id', profile.userId)
          .eq('scheduled_date', newAppt.scheduledDate)
          .neq('status', 'canceled');

        if (recentConflicts && recentConflicts.length > 0) {
          const [sh, sm] = (newAppt.scheduledTime || '09:00').split(':').map(Number);
          const slotStart = sh * 60 + sm;
          const slotEnd = slotStart + (newAppt.durationMinutes || 60);

          const hasOverlap = recentConflicts.some((c: any) => {
            if (effectiveStaffId && c.staff_member_id && c.staff_member_id !== effectiveStaffId) return false;
            if (c.all_day || (c.duration_minutes && c.duration_minutes >= 720)) return true;
            const [ch, cm] = (c.scheduled_time || '09:00').split(':').map(Number);
            const cStart = ch * 60 + cm;
            const cEnd = cStart + (c.duration_minutes || 60);
            return Math.max(slotStart, cStart) < Math.min(slotEnd, cEnd);
          });

          if (hasOverlap) {
            alert('Atenção: Este horário acabou de ser reservado ou bloqueado por outro cliente. Por favor, selecione outro horário disponível.');
            await loadStorefrontData();
            return;
          }
        }

        await supabase.from('appointments').insert({
          id: finalId,
          user_id: profile.userId,
          client_name: newAppt.clientName,
          client_phone: newAppt.clientPhone || null,
          service_name: newAppt.serviceName,
          services_list: newAppt.servicesList || null,
          duration_minutes: newAppt.durationMinutes || 60,
          scheduled_date: newAppt.scheduledDate,
          scheduled_time: newAppt.scheduledTime,
          price: newAppt.price || 0,
          status: 'confirmed',
          payment_status: 'pending',
          created_by_role: 'client_online',
          staff_member_id: effectiveStaffId,
          staff_member_name: newAppt.staffMemberName || null,
          commission_percent: newAppt.commissionPercent || 50,
          notes: newAppt.notes || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

        // 3. Cadastra o cliente automaticamente no CRM do prestador
        if (newAppt.clientName) {
          try {
            await supabase.from('clients').insert({
              user_id: profile.userId,
              name: newAppt.clientName,
              phone: newAppt.clientPhone || null,
              source: 'agendamento_online',
              last_interaction_at: new Date().toISOString()
            });
          } catch (crmErr) {
            console.log('Cliente já existe ou log no CRM:', crmErr);
          }
        }
      } catch (err) {
        console.error('Erro ao persistir agendamento no Supabase:', err);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center mb-4 animate-spin">
          <Loader2 className="h-7 w-7" />
        </div>
        <h2 className="text-base font-bold text-zinc-900">Carregando agendamento...</h2>
        <p className="text-xs text-zinc-500 mt-1">Conectando aos horários disponíveis</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-3xl bg-zinc-100 text-zinc-400 flex items-center justify-center mb-4">
          <Store className="h-8 w-8" />
        </div>
        <h1 className="text-lg font-black text-zinc-900">Página de Agendamento Não Encontrada</h1>
        <p className="text-xs text-zinc-500 max-w-sm mt-1 mb-6">
          O link informado pode ter expirado ou o estabelecimento ainda não ativou a vitrine pública.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white font-bold text-xs rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Ir para o R-MAX</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <DirectBookingPage
      profile={profile || undefined}
      nicheKey={demoNicheKey}
      services={services}
      staffList={staffList}
      existingAppointments={appointments}
      showDemoSwitcher={isDemoPreset}
      onBookAppointment={handleBookAppointment}
      onBack={() => {
        if (window.history.length > 1) {
          window.history.back();
        } else {
          navigate('/');
        }
      }}
    />
  );
}
